import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PlusCircle, Search, Loader2, CheckCircle2, AlertTriangle, Ban, History } from 'lucide-react';
import type { ComputeTariffResult, VwMduRow } from '@mdu-invoicing/shared';
import { useAuth } from '@/hooks/useAuth';
import { apiFetch, ApiError } from '@/lib/api-client';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';

type Stream = 'SSV' | 'TSA';

// Mirrors the API MissedCaseLookup (api types are not importable from the SPA bundle).
interface StreamInfo {
  eligible: boolean;
  reasons: string[];
  tariff: ComputeTariffResult;
  alreadyInWorkingSet: boolean;
}
interface MissedCaseLookup {
  mduId: string;
  status: 'FOUND' | 'NOT_FOUND' | 'OTHER_CONTRACTOR';
  dmpFlag: 'GREEN' | 'RED';
  source: VwMduRow | null;
  ssv: StreamInfo | null;
  tsa: StreamInfo | null;
  alreadyInvoiced: boolean;
}

const eur = new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' });

const REASON_FR: Record<string, string> = {
  NOT_IN_BUILD_LIST: 'pas dans la Build List',
  NOT_MAIN_ADDRESS: 'pas une adresse principale',
  SSV_DONE_NOT_ELIGIBLE: 'statut SSV non éligible',
  NO_SSV_DATE: 'date SSV manquante',
  SSV_DATE_OUT_OF_PERIOD: 'date SSV hors période',
  TSA_DONE_NOT_ELIGIBLE: 'statut TSA non éligible',
  TSA_NOT_SIGNED: 'TSA non signé (Status ≠ Signed)',
  NO_TSA_DATE: 'date TSA manquante',
  TSA_DATE_OUT_OF_PERIOD: 'date TSA hors période',
};

function tariffText(t: ComputeTariffResult): string {
  if (t.status === 'PRICED') return eur.format(t.amountCents / 100);
  if (t.status === 'OUT_OF_SCOPE') return 'hors périmètre';
  return `non chiffré${t.designQuadrant ? ` (quadrant « ${t.designQuadrant} »)` : ''}`;
}

export function AddMissed() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Carry the invoicing month chosen on Éligibles (?from&to) so the missed case lands in —
  // and the user returns to — THAT month, not always the current one (the /add routes fall
  // back to the current month when absent). Éligibles Cause-1 fix (Jeremy 2026-07-08).
  const from = searchParams.get('from');
  const to = searchParams.get('to');
  const periodQS = from && to ? `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}` : '';
  const [mduId, setMduId] = useState('');
  const [looking, setLooking] = useState(false);
  const [lookup, setLookup] = useState<MissedCaseLookup | null>(null);
  const [stream, setStream] = useState<Stream>('SSV');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  // Stale-lookup guards: `reqSeq` ignores a superseded request; `mduIdRef` tracks the
  // latest typed value so a result is never shown for an id the user has since edited.
  const reqSeq = useRef(0);
  const mduIdRef = useRef('');

  const canAdd = lookup !== null && lookup.status !== 'OTHER_CONTRACTOR';
  const streamInfo = lookup ? (stream === 'SSV' ? lookup.ssv : lookup.tsa) : null;

  function resetResult() {
    setLookup(null);
    setComment('');
  }

  async function handleLookup() {
    const id = mduId.trim();
    if (!id || looking) return; // ignore Enter/clicks while a lookup is in flight
    const seq = ++reqSeq.current;
    setLooking(true);
    setLookup(null);
    try {
      const res = await apiFetch<MissedCaseLookup>(`/add/lookup${periodQS}`, {
        method: 'POST',
        body: JSON.stringify({ mduId: id }),
      });
      // Drop a stale response: a newer lookup superseded this one, or the input has
      // changed since the request was sent (never show a result for an edited id).
      if (seq !== reqSeq.current || id !== mduIdRef.current.trim()) return;
      setLookup(res);
      if (res.status === 'OTHER_CONTRACTOR') {
        toast.error('Ce MDU appartient à un autre prestataire');
      } else if (res.status === 'NOT_FOUND') {
        toast.message('MDU non trouvé dans les données synchronisées — saisie manuelle');
      } else {
        toast.success('MDU trouvé');
      }
    } catch (e) {
      if (seq !== reqSeq.current) return;
      toast.error(e instanceof ApiError ? e.message : 'Échec de la validation du MDU_ID');
    } finally {
      if (seq === reqSeq.current) setLooking(false);
    }
  }

  async function handleAdd() {
    if (!lookup || !canAdd) return;
    if (comment.trim() === '') {
      toast.error('Un commentaire est obligatoire pour ajouter un cas manqué');
      return;
    }
    setSaving(true);
    try {
      await apiFetch(`/add${periodQS}`, {
        method: 'POST',
        body: JSON.stringify({ mduId: lookup.mduId, stream, comment: comment.trim() }),
      });
      toast.success(`Cas manqué ajouté — ${lookup.mduId} (${stream})`);
      navigate(`/eligibles${periodQS}`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Échec de l'ajout du cas manqué");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-3xl space-y-5">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Ajout d’un cas manqué</h1>
        <p className="mt-1 text-sm text-slate-500">
          Saisis un <span className="font-mono text-xs">MDU_ID</span> absent de ta liste d’éligibles. Il est validé
          contre la source : s’il y est trouvé (et qu’il t’appartient), ses informations s’affichent ; sinon tu peux
          l’ajouter manuellement. Un commentaire est obligatoire.
        </p>
      </div>

      {/* Lookup */}
      <Card className="p-5">
        <label htmlFor="mduId" className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-400">
          MDU_ID
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <input
            id="mduId"
            type="text"
            value={mduId}
            onChange={(e) => {
              setMduId(e.target.value);
              mduIdRef.current = e.target.value;
              if (lookup) resetResult();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleLookup();
            }}
            placeholder="ex. RUE EXEMPLE_12_VILLE"
            maxLength={64}
            className="min-w-[18rem] flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-sm text-slate-800 outline-none focus:border-brand-purple focus:ring-1 focus:ring-brand-purple"
          />
          <button
            type="button"
            onClick={handleLookup}
            disabled={!mduId.trim() || looking}
            aria-busy={looking}
            className="flex items-center gap-2 rounded-lg bg-brand-gradient px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            {looking ? 'Validation…' : 'Valider'}
          </button>
        </div>
        <p className="mt-2 text-xs text-slate-400">
          Périmètre : <span className="font-semibold text-brand-purple">{user?.contractorName ?? '—'}</span>
        </p>
      </Card>

      {/* Result */}
      {lookup && (
        <div className="space-y-4" aria-live="polite">
          {lookup.status === 'OTHER_CONTRACTOR' ? (
            <Card className="border-rose-200 bg-rose-50/60 p-5">
              <div className="flex items-start gap-3">
                <Ban className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
                <div>
                  <div className="text-sm font-bold text-rose-800">Ajout bloqué</div>
                  <p className="mt-1 text-sm text-rose-700">
                    Le MDU <span className="font-mono text-xs">{lookup.mduId}</span> appartient à un autre
                    prestataire dans la source. Tu ne peux ajouter que des MDU de ton périmètre.
                  </p>
                </div>
              </div>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
                <span className="flex items-center gap-2 text-sm font-bold text-slate-900">
                  <span className="font-mono text-xs text-slate-600">{lookup.mduId}</span>
                </span>
                {lookup.status === 'FOUND' ? (
                  <Badge variant="green" dot>
                    Trouvé (dans la source)
                  </Badge>
                ) : (
                  <Badge variant="red" dot>
                    Non trouvé — saisie manuelle
                  </Badge>
                )}
              </div>

              <div className="space-y-4 px-5 py-4">
                {lookup.alreadyInvoiced && (
                  <div className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    <History className="h-4 w-4 shrink-0" />
                    Ce MDU est déjà présent dans le journal immuable pour ce mois.
                  </div>
                )}

                {lookup.status === 'NOT_FOUND' && (
                  <p className="text-sm text-slate-500">
                    Non présent dans les données synchronisées. Il s’agit peut-être d’un cas récent (la source est
                    synchronisée ~1×/jour). Tu peux l’ajouter quand même ; il sera marqué « à valider » (non chiffré).
                  </p>
                )}

                {lookup.status === 'FOUND' && lookup.source && (
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
                    <Field label="Contractant" value={lookup.source.ssvContractor} mono />
                    <Field label="Design Quadrant" value={lookup.source.designQuadrant} mono />
                    <Field label="Zone (Area_ID)" value={lookup.source.areaId} mono />
                    <Field label="Adresse principale" value={lookup.source.buildingGroupMainAddress} />
                    <Field label="SSV" value={joinStatus(lookup.source.ssvDone, lookup.source.ssvDate)} />
                    <Field label="TSA" value={joinStatus(lookup.source.tsaDone, lookup.source.tsaDate)} />
                  </dl>
                )}

                {/* Stream picker */}
                <div>
                  <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">Flux</div>
                  <div role="radiogroup" aria-label="Flux" className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
                    {(['SSV', 'TSA'] as Stream[]).map((s) => (
                      <button
                        key={s}
                        type="button"
                        role="radio"
                        aria-checked={stream === s}
                        onClick={() => setStream(s)}
                        className={cn(
                          'rounded-lg px-4 py-1.5 text-sm font-semibold transition-colors',
                          stream === s ? 'bg-brand-gradient text-white shadow-sm' : 'text-slate-500 hover:text-slate-800',
                        )}
                      >
                        {s}
                      </button>
                    ))}
                  </div>

                  {/* Per-stream info (FOUND only) */}
                  {streamInfo && (
                    <div className="mt-2 space-y-1 text-xs">
                      <p className="text-slate-500">
                        {streamInfo.eligible ? (
                          <span className="inline-flex items-center gap-1 font-semibold text-emerald-700">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Actuellement éligible {stream}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-slate-500">
                            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> Non éligible {stream}
                            {streamInfo.reasons.length > 0 &&
                              ` — ${streamInfo.reasons.map((r) => REASON_FR[r] ?? r).join(', ')}`}
                          </span>
                        )}
                      </p>
                      <p className="text-slate-500">
                        Tarif estimé : <span className="font-semibold text-slate-700">{tariffText(streamInfo.tariff)}</span>
                      </p>
                      {streamInfo.alreadyInWorkingSet && (
                        <p className="font-medium text-amber-700">
                          Déjà dans ta liste de travail pour ce flux — l’enregistrer le mettra à jour.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Comment (mandatory — OQ-6) */}
                <div>
                  <label htmlFor="comment" className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-400">
                    Commentaire <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    id="comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={2}
                    maxLength={2000}
                    placeholder="Pourquoi ce cas doit-il être facturé ?"
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-brand-purple focus:ring-1 focus:ring-brand-purple"
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleAdd}
                    disabled={saving || comment.trim() === ''}
                    aria-busy={saving}
                    className="flex items-center gap-2 rounded-lg bg-brand-gradient px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusCircle className="h-4 w-4" />}
                    {saving ? 'Ajout…' : 'Ajouter le cas manqué'}
                  </button>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className={cn('mt-0.5 text-slate-800', mono && 'font-mono text-xs')}>{value || '—'}</dd>
    </div>
  );
}

function joinStatus(done: string | null, date: string | null): string | null {
  const parts = [done, date].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}
